import {isValidGraduationYearFilter} from '../graduation-year/is-valid-graduation-year';

/**
 * Shareable store-page selection.
 *
 * The server still owns the store (`specialId`), the locked institution
 * (`InstitutionID`), and `__prefilter`. Those never come from the query string.
 *
 * | Param    | Meaning                                                                 | Omitted when                                      |
 * |----------|-------------------------------------------------------------------------|---------------------------------------------------|
 * | `s`      | School search. Existing links keep working.                             | Empty                                             |
 * | `t`      | `best-sellers`, `single-tassel`, `diploma-frames`, `certificate-frames` | Redundant with `__prefilter`, or more than one frame type |
 * | `ft`     | Comma-separated frame types: `tassel`, `diploma`, `certificate`         | One legacy `t` value can represent it, or it matches `__prefilter` |
 * | `finish` | Comma-separated moulding names                                          | None selected                                     |
 * | `mat`    | Comma-separated mat names                                               | None selected. The store also clears it on a best-sellers path, where the mat list is hidden. |
 * | `min`    | Minimum price in dollars, whole number 0–500                            | `0`                                               |
 * | `max`    | Maximum price in dollars, whole number 0–500                            | `500`                                             |
 * | `sort`   | `0` Featured, `1` price asc, `2` price desc, `3` best selling, `4` reviews, `5` fast ship | Empty (the initial "no sort" value). Portal 3 rejects `4` and `5`. |
 * | `page`   | 1-based page. Non-integers and values outside 1–10000 become page 1.    | `1`                                               |
 * | `year`   | Graduation year, 1900–2100                                              | Empty, invalid, or the year box is not on this portal (only portal 3 shows it). |
 *
 * Any other parameter (for example `utm_source`) is left in place.
 * `t=best-sellers` stays in `t` when a frame type is also selected; the frame
 * type is then written to `ft` because `t` can hold only one value.
 * Repeated `mat` or `finish` keys are combined. Names not in the server-provided
 * option list are dropped. When that list is missing, names are kept so a
 * shared link still works before the first product response builds the options.
 */

export const DEFAULT_PRICE_MIN = 0;
export const DEFAULT_PRICE_MAX = 500;
export const DEFAULT_PAGE = 1;
export const MAX_PAGE = 10000;

export const STORE_FRAME_TYPES = ['tassel', 'diploma', 'certificate'] as const;
export type StoreFrameType = (typeof STORE_FRAME_TYPES)[number];

const LEGACY_FRAME_PARAM: Record<StoreFrameType, string> = {
    tassel: 'single-tassel',
    diploma: 'diploma-frames',
    certificate: 'certificate-frames',
};

const MANAGED_PARAMS = ['s', 't', 'ft', 'finish', 'mat', 'min', 'max', 'sort', 'page', 'year'] as const;

const SORT_VALUES = ['0', '1', '2', '3', '4', '5'] as const;
const PORTAL3_HIDDEN_SORTS = new Set(['4', '5']);

export type StoreSelection = {
    inst: string;
    sort: string;
    pageNumber: number;
    priceMin: number;
    priceMax: number;
    finish: string[];
    matcombo: string[];
    frameType: StoreFrameType[];
    /** True only when the URL itself asks for best sellers (`t=best-sellers`). */
    bestSellers: boolean;
    graduationYear: string;
};

export type StoreSelectionParseOptions = {
    matOptions?: string[];
    mouldingOptions?: string[];
    portalId?: number;
};

export type StoreSelectionBaseline = {
    prefilter: number;
};

export function prefilterFrameTypes(prefilter: number): StoreFrameType[] {
    switch (prefilter) {
        case 2:
            return ['diploma'];
        case 3:
            return ['certificate'];
        case 4:
            return ['tassel'];
        default:
            return [];
    }
}

export function emptyStoreSelection(): StoreSelection {
    return {
        inst: '',
        sort: '',
        pageNumber: DEFAULT_PAGE,
        priceMin: DEFAULT_PRICE_MIN,
        priceMax: DEFAULT_PRICE_MAX,
        finish: [],
        matcombo: [],
        frameType: [],
        bestSellers: false,
        graduationYear: '',
    };
}

export function parseStoreSelection(params: URLSearchParams, options: StoreSelectionParseOptions = {}): StoreSelection {
    const selection = emptyStoreSelection();
    selection.inst = params.get('s') ?? '';
    selection.bestSellers = params.get('t') === 'best-sellers';
    selection.frameType = parseFrameTypes(params);
    selection.finish = parseNameList(combinedParam(params, 'finish'), options.mouldingOptions);
    selection.matcombo = parseNameList(combinedParam(params, 'mat'), options.matOptions);
    const prices = parsePrices(params.get('min'), params.get('max'));
    selection.priceMin = prices.min;
    selection.priceMax = prices.max;
    selection.sort = parseSort(params.get('sort'), options.portalId);
    selection.pageNumber = parsePage(params.get('page'));
    const year = params.get('year') ?? '';
    selection.graduationYear = isValidGraduationYearFilter(year) ? year : '';
    return selection;
}

export function serializeStoreSearch(currentSearch: string, state: StoreSelection, baseline: StoreSelectionBaseline): string {
    const next = new URLSearchParams(currentSearch);
    for (const key of MANAGED_PARAMS) {
        next.delete(key);
    }

    if (state.inst) {
        next.set('s', state.inst);
    }

    const lockedFrames = prefilterFrameTypes(baseline.prefilter);
    const frames = uniqueFrameTypes(state.frameType);
    const framesDiffer = !sameFrames(frames, lockedFrames);
    const writeBestSellers = state.bestSellers && baseline.prefilter !== 1;

    if (writeBestSellers) {
        next.set('t', 'best-sellers');
    }

    if (framesDiffer && frames.length > 0) {
        if (!writeBestSellers && frames.length === 1) {
            next.set('t', LEGACY_FRAME_PARAM[frames[0]]);
        } else {
            next.set('ft', frames.join(','));
        }
    }

    if (state.finish.length > 0) {
        next.set('finish', state.finish.join(','));
    }
    if (state.matcombo.length > 0) {
        next.set('mat', state.matcombo.join(','));
    }
    if (state.priceMin !== DEFAULT_PRICE_MIN) {
        next.set('min', String(state.priceMin));
    }
    if (state.priceMax !== DEFAULT_PRICE_MAX) {
        next.set('max', String(state.priceMax));
    }
    if (state.sort) {
        next.set('sort', state.sort);
    }
    if (state.pageNumber > DEFAULT_PAGE) {
        next.set('page', String(state.pageNumber));
    }
    if (state.graduationYear) {
        next.set('year', state.graduationYear);
    }

    const query = next.toString();
    return query ? `?${query}` : '';
}

/** True when the query string would actually change, ignoring parameter order and encoding. */
export function storeSearchChanged(currentSearch: string, nextSearch: string): boolean {
    return canonicalSearch(currentSearch) !== canonicalSearch(nextSearch);
}

export function splitAppliedList(value: string): string[] {
    return parseNameList(value, undefined);
}

type StoreSelectionHistorySource = {
    restoreFromLocation: () => void;
};

/** Browser Back/Forward. The store restores; this listener must not write history. */
export function bindStoreSelectionHistory(store: StoreSelectionHistorySource): () => void {
    const onPopState = () => {
        store.restoreFromLocation();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
}

function combinedParam(params: URLSearchParams, key: string): string | null {
    const values = params.getAll(key);
    if (values.length === 0) {
        return null;
    }
    return values.join(',');
}

function parseFrameTypes(params: URLSearchParams): StoreFrameType[] {
    const fromFt = parseNameList(combinedParam(params, 'ft'), [...STORE_FRAME_TYPES]).filter(isStoreFrameType);
    if (fromFt.length > 0) {
        return fromFt;
    }

    switch (params.get('t')) {
        case 'single-tassel':
            return ['tassel'];
        case 'diploma-frames':
            return ['diploma'];
        case 'certificate-frames':
            return ['certificate'];
        default:
            return [];
    }
}

function parseNameList(raw: string | null, allowed: string[] | undefined): string[] {
    if (!raw) {
        return [];
    }

    const tokens = raw
        .split(',')
        .map(part => part.trim())
        .filter(part => part.length > 0);
    const allowedNames = allowedNameSet(allowed);
    const names: string[] = [];
    const seen = new Set<string>();

    for (const token of tokens) {
        const name = allowedNames ? allowedNames.get(token) : token;
        if (!name || seen.has(name)) {
            continue;
        }
        seen.add(name);
        names.push(name);
    }

    return names;
}

function allowedNameSet(allowed: string[] | undefined): Map<string, string> | null {
    if (!Array.isArray(allowed)) {
        return null;
    }

    const names = new Map<string, string>();
    for (const name of allowed) {
        if (typeof name !== 'string' || name.trim() === '') {
            continue;
        }
        names.set(name, name);
    }
    return names;
}

function parsePrices(rawMin: string | null, rawMax: string | null): {min: number; max: number} {
    const min = parsePriceBound(rawMin);
    const max = parsePriceBound(rawMax);
    const priceMin = min ?? DEFAULT_PRICE_MIN;
    const priceMax = max ?? DEFAULT_PRICE_MAX;

    if (priceMin > priceMax) {
        return {min: DEFAULT_PRICE_MIN, max: DEFAULT_PRICE_MAX};
    }

    return {min: priceMin, max: priceMax};
}

function parsePriceBound(raw: string | null): number | null {
    if (raw == null || raw === '') {
        return null;
    }
    if (!/^\d+$/.test(raw)) {
        return null;
    }

    const value = Number(raw);
    if (!Number.isInteger(value) || value < DEFAULT_PRICE_MIN || value > DEFAULT_PRICE_MAX) {
        return null;
    }

    return value;
}

function parseSort(raw: string | null, portalId: number | undefined): string {
    if (!raw || !SORT_VALUES.includes(raw as (typeof SORT_VALUES)[number])) {
        return '';
    }
    if (portalId === 3 && PORTAL3_HIDDEN_SORTS.has(raw)) {
        return '';
    }
    return raw;
}

function parsePage(raw: string | null): number {
    if (raw == null || raw === '') {
        return DEFAULT_PAGE;
    }
    if (!/^\d+$/.test(raw)) {
        return DEFAULT_PAGE;
    }

    const page = Number(raw);
    if (!Number.isSafeInteger(page) || page < DEFAULT_PAGE || page > MAX_PAGE) {
        return DEFAULT_PAGE;
    }

    return page;
}

function uniqueFrameTypes(values: readonly string[]): StoreFrameType[] {
    const frames: StoreFrameType[] = [];
    for (const value of values) {
        if (!isStoreFrameType(value) || frames.includes(value)) {
            continue;
        }
        frames.push(value);
    }
    return frames;
}

function isStoreFrameType(value: string): value is StoreFrameType {
    return (STORE_FRAME_TYPES as readonly string[]).includes(value);
}

function sameFrames(left: readonly StoreFrameType[], right: readonly StoreFrameType[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function canonicalSearch(search: string): string {
    const params = new URLSearchParams(search);
    const pairs: string[] = [];
    params.forEach((value, key) => {
        pairs.push(`${key}=${value}`);
    });
    pairs.sort();
    return pairs.join('&');
}
